import { Tooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import {
  ChevronDown,
  ChevronUp,
  EllipsisVertical,
  LoaderCircle,
} from "lucide-react";
import React, {
  type ComponentPropsWithoutRef,
  useCallback,
  useState,
} from "react";

import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  type DropdownMenuItemVariant,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { BodyText, HeaderText } from "@/components/ui/Text";

import {
  CollapsibleContent,
  Collapsible as CollapsibleRoot,
  CollapsibleTrigger,
} from "./CollapsibleComponents";

export type ActionMenuItem = {
  label: string;
  onClick: () => void;
  icon: React.ReactNode;
  shortcut?: string;
  disabled?: boolean;
  variant?: DropdownMenuItemVariant;
};

interface CollapsibleProps
  extends VariantProps<typeof collapsibleVariants>,
    Omit<
      ComponentPropsWithoutRef<typeof CollapsibleRoot>,
      "open" | "defaultOpen" | "onOpenChange" | "children"
    > {
  children: React.ReactNode;
  actionMenu?: ActionMenuItem[];
  forcedDropdownActions?: ActionMenuItem[];
  dropdownActionsLoading?: boolean;
  headerSize?: "sm" | "md" | "lg";
  headerTextColor?: "link" | "error" | "superclinical";
  title: string;
  titleIcon?: React.ReactNode;
  titleAction?: React.ReactNode;
  defaultOpen?: boolean;
  status?: string;
  statusBadge?: React.ReactNode;
  customActions?: React.ReactNode[];
  background?: "default" | "secondary" | "danger" | "superclinical";
  className?: string;
  badgeComponents?: React.ReactNode[];
  allowOverflow?: boolean;
  onOpenChange?: (open: boolean) => void;
  controlled?: boolean;
  open?: boolean;
  showActionTooltips?: boolean;
  testId?: string;
}

const collapsibleVariants = cva("flex flex-col gap-2 border p-3 rounded-md", {
  variants: {
    background: {
      default: "bg-primary",
      secondary: "bg-secondary",
      danger: "bg-error",
      superclinical: "bg-superclinical",
    },
    border: {
      default: "border-primary",
      none: "border-none p-0",
    },
  },
});

const splitActions = (
  actions: ActionMenuItem[],
): [ActionMenuItem[], ActionMenuItem[]] => {
  if (actions.length <= 2) {
    return [actions, []];
  }
  return [actions.slice(0, 2), actions.slice(2)];
};

const Collapsible = ({
  children,
  headerSize = "md",
  headerTextColor = "link",
  actionMenu = [],
  forcedDropdownActions = [],
  dropdownActionsLoading = false,
  title,
  titleIcon,
  titleAction,
  status,
  statusBadge,
  customActions = [],
  badgeComponents,
  background = "default",
  border = "default",
  defaultOpen = false,
  className,
  allowOverflow = false,
  onOpenChange,
  controlled,
  open,
  showActionTooltips = false,
  testId,
  ...rest
}: CollapsibleProps) => {
  const [internalOpen, setInternalOpen] = useState(!!defaultOpen || !!open);

  const [visibleActions, dropdownActions] = splitActions(actionMenu);
  dropdownActions.push(...forcedDropdownActions);

  // maps to @components/Text/HeaderText.tsx headerSize line-heights. Allows chevron positioning to be the center of the first line for all text-sizes
  const iconSize = {
    xxxl: "h-[58px]",
    xxl: "h-[39px]",
    xl: "h-[29px]",
    lg: "h-[24px]",
    md: "h-[19px]",
    sm: "h-[15px]",
  };

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (onOpenChange) onOpenChange(newOpen);
      setInternalOpen(newOpen);
    },
    [onOpenChange],
  );

  const colorClasses = {
    link: "text-link",
    error: "text-error",
    superclinical: "text-superclinical",
  };

  return (
    <CollapsibleRoot
      defaultOpen={defaultOpen}
      open={(open && controlled) || (!controlled && internalOpen)}
      onOpenChange={handleOpenChange}
      {...rest}
    >
      <div
        className={cn(collapsibleVariants({ background, border, className }))}
      >
        <div className="flex flex-col gap-2">
          <div className="w-full flex flex-row justify-between gap-2 items-center">
            <div className="flex items-center gap-2">
              <CollapsibleTrigger
                className="cursor-pointer"
                data-testid="collapsible-header"
              >
                <div
                  className={cn(
                    "flex items-center gap-1",
                    colorClasses[headerTextColor] || colorClasses.link,
                  )}
                >
                  {(open && controlled) || (!controlled && internalOpen) ? (
                    <ChevronUp
                      size={16}
                      className={cn("relative shrink-0", iconSize[headerSize])}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      className={cn("relative shrink-0", iconSize[headerSize])}
                    />
                  )}
                  {titleIcon}
                  <HeaderText
                    textColor={headerTextColor || "link"}
                    size={headerSize}
                    className="text-start cursor-pointer"
                  >
                    {title}
                  </HeaderText>
                </div>
              </CollapsibleTrigger>
              {titleAction}
            </div>
            <div className="flex justify-end items-center gap-3">
              {statusBadge}
              {status && (
                <BodyText textColor="tertiary" size="sm" wrap={false}>
                  {status}
                </BodyText>
              )}
              {customActions}
              {visibleActions.map((action) => {
                const icon = action.icon as React.ReactElement<{
                  className?: string;
                }>;
                const actionButton = (
                  <Button
                    key={action.label}
                    className="!p-0"
                    variant="icon"
                    size="sm"
                    onClick={action.onClick}
                    disabled={action.disabled}
                  >
                    {React.cloneElement(icon, {
                      className: cn(
                        `${action.disabled ? "cursor-not-allowed" : "cursor-pointer"} stroke-link`,
                        icon.props.className,
                      ),
                    })}
                  </Button>
                );

                if (showActionTooltips) {
                  return (
                    <Tooltip
                      tooltipContentText={action.label}
                      key={action.label}
                      triggerClassName="leading-[0px]"
                    >
                      {actionButton}
                    </Tooltip>
                  );
                }
                return actionButton;
              })}
              {dropdownActions.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    {dropdownActionsLoading ? (
                      <LoaderCircle
                        className="cursor-pointer stroke-link animate-spin w-full h-full"
                        size={16}
                        data-testid={`collapsible-dropdown-spinner${testId ? `-${testId}` : ""}`}
                      />
                    ) : (
                      <EllipsisVertical
                        size={16}
                        className="cursor-pointer stroke-link"
                        data-testid={`collapsible-dropdown-button${testId ? `-${testId}` : ""}`}
                      />
                    )}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-fit-content">
                    <DropdownMenuGroup>
                      {dropdownActions.map((action) => (
                        <DropdownMenuItem
                          key={action.label}
                          onClick={action.onClick}
                          disabled={action.disabled}
                          variant={action.variant}
                          className="gap-2"
                        >
                          {action.icon}
                          {action.label}
                          {action.shortcut && (
                            <DropdownMenuShortcut>
                              {action.shortcut}
                            </DropdownMenuShortcut>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
          {!!badgeComponents?.length && (
            <div className="flex items-center gap-2 flex-wrap">
              {badgeComponents}
            </div>
          )}
        </div>
        <CollapsibleContent
          className={cn(
            "overflow-hidden transition-all data-[state=closed]:animate-slide-up data-[state=open]:animate-slide-down",
            allowOverflow && "data-[state=open]:overflow-visible",
          )}
        >
          {children}
        </CollapsibleContent>
      </div>
    </CollapsibleRoot>
  );
};

export { Collapsible };
